import { api, API_V2_BASE_URL } from '@homeviews/store'
import type { TemplateSlugs } from '@homeviews/outreach'

type EmailPreviewQueryParams = {
  template: TemplateSlugs
}
type EmailPreviewResponse = {
  html: string
  subject: string
}

type GetRequestsQueryParams = {
  page: number
  perPage: number
}
type GetRequestsResponse = {
  data: Array<{
    title: string
    development: {
      name: string
    }
    type: string
    createdAt: string
    openRate: number
    ctr: number
    completionRate: number
  }>
  meta: {
    last_page: number
    total: number
  }
}

type DevelopmentsResponse = {
  data: Array<{
    id: number
    name: string
  }>
}

type GetDocumentParams = {
  documentId: number
}
type CreateDocumentParams = {
  documentFormData: FormData
}
type DocumentResponse = {
  data: {
    id: number
    status: 'pending' | 'validating' | 'validated' | 'error'
    errors: {
      document: Array<string>
    }
    message: string | null
  }
}

type CreateRequestsParams = {
  template: string
  payload: Array<{
    documentId: number
    developmentId: number
  }>
}
type CreateRequestsResponse = {
  message: Array<string> | undefined
}

// https://redux-toolkit.js.org/rtk-query/usage/code-splitting
export const outreachApi = api.injectEndpoints({
  endpoints: (build) => ({
    getOutreachEmailPreview: build.query<
      EmailPreviewResponse,
      EmailPreviewQueryParams
    >({
      query: ({ template }: EmailPreviewQueryParams) => {
        return {
          url: `${API_V2_BASE_URL}/resident-outreach/preview`,
          method: 'GET',
          params: {
            template: template as string,
          },
        }
      },
    }),
    getOutreachRequests: build.query<
      GetRequestsResponse,
      GetRequestsQueryParams
    >({
      query: ({ page, perPage }: GetRequestsQueryParams) => {
        return {
          url: `${API_V2_BASE_URL}/resident-outreach/requests`,
          method: 'GET',
          params: {
            page: page,
            per_page: perPage,
          },
        }
      },
    }),
    getOutreachDevelopments: build.query<DevelopmentsResponse, null>({
      query: () => {
        return {
          url: `${API_V2_BASE_URL}/developments`,
          method: 'GET',
          params: {
            forResidentOutreach: true,
            per_page: 100, // Developers have less-than 100 Developments, so this gets all results
          },
        }
      },
    }),
    createOutreachDocument: build.mutation<
      DocumentResponse,
      CreateDocumentParams
    >({
      query: ({ documentFormData }: CreateDocumentParams) => {
        return {
          url: `${API_V2_BASE_URL}/resident-outreach/documents`,
          method: 'POST',
          body: documentFormData,
          headers: {
            Accept: 'application/json',
          },
        }
      },
    }),
    getOutreachDocument: build.query<DocumentResponse, GetDocumentParams>({
      query: ({ documentId }: GetDocumentParams) => {
        return {
          url: `${API_V2_BASE_URL}/resident-outreach/documents/${documentId}`,
          method: 'GET',
        }
      },
    }),
    createOutreachRequests: build.mutation<
      CreateRequestsResponse,
      CreateRequestsParams
    >({
      query: ({ template, payload }: CreateRequestsParams) => {
        return {
          url: `${API_V2_BASE_URL}/resident-outreach/requests`,
          method: 'POST',
          body: {
            template: template,
            payload: payload,
          },
          headers: {
            Accept: 'application/json',
          },
        }
      },
    }),
  }),
})

export const {
  // https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#usequery
  useGetOutreachEmailPreviewQuery,
  useGetOutreachRequestsQuery,
  useGetOutreachDevelopmentsQuery,
  // https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#usemutation
  useCreateOutreachDocumentMutation,
  // https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#uselazyquery
  useLazyGetOutreachDocumentQuery,
  useCreateOutreachRequestsMutation,
} = outreachApi
